<template>
  <section class="bg-tw-cream" aria-labelledby="guide-heading">
    <div class="mx-auto max-w-[1280px] px-6 py-20 md:px-[98px] md:py-[53px]">
      <p class="inline-block bg-tw-rust px-1.5 text-[17px] font-semibold uppercase leading-7 text-white">{{ t('eyebrow') }}</p>
      <h2 id="guide-heading" class="mt-8 max-w-[546px] text-4xl tracking-[-0.02em] text-black md:text-[45px] md:leading-[1.2]">{{ t('heading') }}</h2>
      <p class="mt-8 max-w-[761px] text-xl leading-7 text-tw-ink md:text-[25px]">{{ t('lead') }}</p>

      <div class="mt-10 max-w-[994px] border-b border-black text-tw-ink">
        <details v-for="item in items" :key="item.key" class="group border-t border-black">
          <summary
            class="flex cursor-pointer list-none items-center justify-between gap-6 px-4 py-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-tw-rust [&::-webkit-details-marker]:hidden"
          >
            <span>
              <span class="block text-2xl font-semibold leading-7 md:text-3xl">{{ t(`${item.key}.title`) }}</span>
              <span class="mt-5 block text-lg leading-8 md:text-xl">{{ t(`${item.key}.subtitle`) }}</span>
            </span>
            <svg
              class="h-6 w-6 shrink-0 text-tw-rust transition-transform group-open:rotate-45 motion-reduce:transition-none"
              viewBox="0 0 25 25"
              aria-hidden="true"
            >
              <path d="M0 10h25v5H0zM10 0h5v25h-5z" fill="currentColor" />
            </svg>
          </summary>
          <div class="max-w-[844px] px-4 pb-10 text-lg leading-8 md:text-xl">
            <p v-for="p in item.paragraphs" :key="p" class="mb-8">{{ t(`${item.key}.${p}`) }}</p>
            <ul class="space-y-2 font-semibold">
              <li v-for="link in item.links" :key="link.label">
                <a
                  :href="link.href"
                  :target="link.external ? '_blank' : undefined"
                  :rel="link.external ? 'noopener noreferrer' : undefined"
                  class="underline hover:text-tw-rust focus-visible:outline focus-visible:outline-2 focus-visible:outline-tw-rust"
                >
                  {{ t(`${item.key}.${link.label}`) }}
                  <span aria-hidden="true">{{ link.external ? '↗' : '→' }}</span>
                  <span v-if="link.external" class="sr-only">({{ t('newTab') }})</span>
                </a>
              </li>
            </ul>
          </div>
        </details>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'

  interface GuideLink {
    label: string
    href: string
    external: boolean
  }

  interface GuideItem {
    key: string
    paragraphs: string[]
    links: GuideLink[]
  }

  const items: GuideItem[] = [
    {
      key: 'engage',
      paragraphs: ['p1', 'p2'],
      links: [{ label: 'link1', href: 'https://www.uio.no/tjenester/it/utdanning/samtavla/index.html', external: true }],
    },
    {
      key: 'dialogic',
      paragraphs: ['p1', 'p2'],
      links: [{ label: 'link1', href: 'https://www.uv.uio.no/iped/tjenester/ressurser-for-muntlige-ferdigheter/', external: true }],
    },
    {
      key: 'research',
      paragraphs: ['p1', 'p2'],
      links: [
        {
          label: 'link1',
          href: 'https://www.uv.uio.no/iped/forskning/prosjekter/rasmussen-innovasjonsprosjektet-samtavla/index.html',
          external: true,
        },
        { label: 'link2', href: 'mailto:support@talkwall.uio.no', external: false },
      ],
    },
  ]

  const { t } = useI18n({
    messages: {
      no: {
        eyebrow: 'For undervisere',
        heading: 'Hvordan bruke Samtavla i undervisningen?',
        lead: 'Slik kan Samtavla støtte dialog, deltakelse og felles forståelse i undervisningen.',
        newTab: 'åpnes i ny fane',
        engage: {
          title: 'Engasjere og involvere',
          subtitle: 'Lærerens rolle i den felles læringssamtalen',
          p1: 'Samtavla er laget for å hjelpe forelesere med å engasjere studenter i felles læringssamtaler, der vi deler, vurderer og utvikler forståelse sammen.',
          p2: 'Det avgjørende er hvordan læreren bruker verktøyet. Ved å kombinere pedagogisk praksis og teknologi kan læreren legge til rette for dialog i ulike undervisningsopplegg. Samtavla støtter samtalen og erstatter ikke det faglige innholdet.',
          link1: 'Les mer om å engasjere og involvere',
        },
        dialogic: {
          title: 'Dialogisk undervisning',
          subtitle: 'Lytt, reflekter og bygg videre på hverandres bidrag',
          p1: 'Samtavla er laget for å støtte dialogisk undervisning. Både læreren og elevene bidrar til samtalen, slik at elevenes tenkning om et tema utvikles videre.',
          p2: 'Elevene oppmuntres til å delta aktivt og får hjelp til å sette ord på, reflektere over og justere sin forståelse. Lærere og elever lytter til hverandre og bygger videre på det andre bringer inn i samtalen.',
          link1: 'Utforsk ressurser for dialogisk undervisning',
        },
        research: {
          title: 'Et forskningsbasert verktøy',
          subtitle: 'Samtavla er basert på forskning og verktøyet er utviklet over tid og med støtte fra mange samarbeidspartnere.',
          p1: 'Tjenesten tilbys gratis, og det er ingen kommersielle interesser bak Samtavla.',
          p2: 'Tilbakemeldinger fra dem som bruker Samtavla er viktige for videre utvikling. Ta kontakt dersom noe ikke fungerer som det skal, eller hvis du har forslag til forbedringer.',
          link1: 'Les om forskningen bak Samtavla',
          link2: 'Send oss en e-post',
        },
      },
      en: {
        eyebrow: 'For teachers',
        heading: 'How to use Talkwall in teaching?',
        lead: 'How Talkwall can support dialogue, participation and shared understanding in teaching.',
        newTab: 'opens in a new tab',
        engage: {
          title: 'Engage and involve',
          subtitle: "The teacher's role in the shared learning conversation",
          p1: 'Talkwall is designed to help lecturers engage students in shared learning conversations, where we share, evaluate and develop understanding together.',
          p2: 'What matters is how the teacher uses the tool. By combining teaching practice and technology, the teacher can facilitate dialogue in different teaching settings. Talkwall supports the conversation and does not replace the subject content.',
          link1: 'Read more about engaging and involving',
        },
        dialogic: {
          title: 'Dialogic teaching',
          subtitle: "Listen, reflect and build on each other's contributions",
          p1: "Talkwall is designed to support dialogic teaching. Both the teacher and the pupils contribute to the conversation, so that the pupils' thinking about a topic develops further.",
          p2: 'Pupils are encouraged to participate actively and are helped to put into words, reflect on and adjust their understanding. Teachers and pupils listen to each other and build on what others bring into the conversation.',
          link1: 'Explore resources for dialogic teaching',
        },
        research: {
          title: 'A research-based tool',
          subtitle: 'Talkwall is based on research and has been developed over time with support from many partners.',
          p1: 'The service is free, and there are no commercial interests behind Talkwall.',
          p2: 'Feedback from the people who use Talkwall is important for further development. Get in touch if something is not working as it should, or if you have suggestions for improvements.',
          link1: 'Read about the research behind Talkwall',
          link2: 'Send us an email',
        },
      },
    },
  })
</script>
